import React, { useMemo, useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Gantt, Willow, WillowDark } from "@svar-ui/react-gantt";
import { SelectButton } from "primereact/selectbutton";
import { Dropdown } from "primereact/dropdown";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import axios from "axios";
import "@svar-ui/react-gantt/all.css";

import ProductionMetricsMeasuresSummary from "../productionmetricsmeasuressummary/ProductionMetricsMeasuresSummary";
import ProductionWorkflowMetricsForm from "../productionmetricsmeasuressummary/ProductionWorkflowMetricsForm";

const ProductionItemsComponent = (props) => {
  const { t } = props;

  const VIEW_TYPE_STORAGE_KEY = "productionItemsViewType";
  const GANTT_HORIZON_STORAGE_KEY = "productionItemsGanttHorizonDays";
  const GANTT_SCALE_STORAGE_KEY = "productionItemsGanttScaleMode";

  const [dark, setDark] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectItems, setProjectItems] = useState([]);
  const [projectItemStatuses, setProjectItemStatuses] = useState([]);
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem(VIEW_TYPE_STORAGE_KEY) || "gantt";
  });

  const [ganttHorizonDays, setGanttHorizonDays] = useState(() => {
    const saved = parseInt(localStorage.getItem(GANTT_HORIZON_STORAGE_KEY), 10);
    return Number.isFinite(saved) && saved > 0 ? saved : 14;
  });

  const [ganttScaleMode, setGanttScaleMode] = useState(() => {
    return localStorage.getItem(GANTT_SCALE_STORAGE_KEY) || "hour";
  });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [draggedItemId, setDraggedItemId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);

  const viewTypeOptions = [
    { label: "GANTT", value: "gantt" },
    { label: "Kanban", value: "kanban" }
  ];

  const ganttScaleOptions = [
    { label: "Órás", value: "hour" },
    { label: "Napos", value: "day" }
  ];

  const ganttHorizonOptions = [
    { label: "2 nap", value: 2 },
    { label: "7 nap", value: 7 },
    { label: "14 nap", value: 14 },
    { label: "30 nap", value: 30 },
    { label: "60 nap", value: 60 }
  ];

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("userToken"),
        "App-Network": localStorage.getItem("networkId")
      }
    });
  };

  const getSelectedProjectItemId = () => {
    return (
      selectedItem?.id ||
      selectedItem?.project_item?.id ||
      selectedItem?.projectItem?.id ||
      null
    );
  };

  const handleViewTypeChange = (value) => {
    if (!value) return;

    setViewType(value);
    localStorage.setItem(VIEW_TYPE_STORAGE_KEY, value);
  };

  const handleGanttScaleModeChange = (value) => {
    if (!value) return;
    setGanttScaleMode(value);
    localStorage.setItem(GANTT_SCALE_STORAGE_KEY, value);
  };

  const handleGanttHorizonChange = (value) => {
    const parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return;
    }
    setGanttHorizonDays(parsedValue);
    localStorage.setItem(GANTT_HORIZON_STORAGE_KEY, parsedValue.toString());
  };

  const getStatusId = (item) => {
    return (
      item?.project_item_status?.id ||
      item?.project_item_status_id ||
      item?.status?.id ||
      item?.status ||
      null
    );
  };

  const getStatusName = (item) => {
    return (
      item?.project_item_status?.name ||
      projectItemStatuses.find((status) => Number(status.id) === Number(getStatusId(item)))?.name ||
      "Nincs státusz"
    );
  };

  const getStatusLabelById = (statusId) => {
    const found = projectItemStatuses.find((item) => Number(item.id) === Number(statusId));

    return found?.name || "Nincs státusz";
  };

  const getProjectItemStatusSeverity = (statusName) => {
    const name = statusName ? statusName.toString().toLowerCase() : "";

    if (name.includes("új")) {
      return "primary";
    }

    if (name.includes("folyamat")) {
      return "info";
    }

    if (name.includes("szünet")) {
      return "warning";
    }

    if (name.includes("kész")) {
      return "success";
    }

    if (name.includes("törölt")) {
      return "danger";
    }

    return null;
  };

  const renderProjectItemStatusBadge = (itemOrStatusName) => {
    const label =
      typeof itemOrStatusName === "string"
        ? itemOrStatusName
        : getStatusName(itemOrStatusName);

    return (
      <Tag
        value={label}
        severity={getProjectItemStatusSeverity(label)}
        rounded
      />
    );
  };

  const getDateValue = (value) => {
    if (!value) return "-";
    return window.App.formatDateValue(value);
  };

  const parseDate = (str) => {
    if (!str) return null;

    return new Date(str.toString().replace(" ", "T"));
  };

  const addOneDay = (date) => {
    if (!date) return null;

    const next = new Date(date);
    next.setDate(next.getDate() + 1);

    return next;
  };

  const getItemStartDate = (item) => {
    return (
      parseDate(item?.scheduled_from) ||
      parseDate(item?.planned_from) ||
      parseDate(item?.start_date) ||
      parseDate(item?.created_at)
    );
  };

  const getItemEndDate = (item) => {
    const start = getItemStartDate(item);
    return (
      parseDate(item?.scheduled_until) ||
      parseDate(item?.planned_until) ||
      parseDate(item?.deadline) ||
      parseDate(item?.updated_at) ||
      addOneDay(start)
    );
  };

  const getProjectItemTitle = (item) => {
    return item?.name || item?.project?.name || "Ismeretlen tétel";
  };

  const getProjectItemSubtitle = (item) => {
    const parts = [];

    if (item?.project?.name) {
      parts.push(item.project.name);
    }

    if (item?.external_reference) {
      parts.push("#" + item.external_reference);
    }

    return parts.length > 0 ? parts.join(" — ") : "-";
  };

  const loadProjectItem = (id) => {
    if (!id) return;

    setDialogVisible(true);
    setDialogLoading(true);
    setSelectedItem(null);

    apiCall("get", `/api/v2/project-item/${id}`)
      .then((response) => {
        setSelectedItem(response?.data?.data || null);
      })
      .catch((error) => {
        window.App.toastShow("error", t("error"), error.toString(), "", error.response);
        setSelectedItem(null);
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const updateProjectItemStatus = (projectItemId, statusId) => {
    if (!projectItemId || !statusId || statusUpdating) return;

    const item = projectItems.find((row) => Number(row.id) === Number(projectItemId));
    if (!item) return;

    const currentStatusId = getStatusId(item);

    if (Number(currentStatusId) === Number(statusId)) {
      setDraggedItemId(null);
      return;
    }

    setStatusUpdating(true);

    apiCall("patch", `/api/v2/project-item/${projectItemId}`, {
      project_item_status: Number(statusId)
    })
      .then(() => {
        const nextStatus = projectItemStatuses.find((status) => Number(status.id) === Number(statusId));

        setProjectItems((prevItems) => prevItems.map((row) => {
          if (Number(row.id) !== Number(projectItemId)) {
            return row;
          }

          return {
            ...row,
            project_item_status: nextStatus || {
              id: Number(statusId),
              name: getStatusLabelById(statusId)
            }
          };
        }));

        window.App.toastShow("success", "Státusz módosítva!");
      })
      .catch((error) => {
        window.App.toastShow("error", t("error"), error.toString(), "", error.response);
      })
      .finally(() => {
        setStatusUpdating(false);
        setDraggedItemId(null);
      });
  };

  useEffect(() => {
    apiCall("get", "/api/v2/project-item-status?sort=sort&direction=asc&item_per_page=1000")
      .then((response) => {
        const items = response?.data?.data?.items || [];
        setProjectItemStatuses(items);
      })
      .catch((error) => {
        window.App.toastShow("error", t("error"), error.toString(), "", error.response);
      });
  }, []);

  useEffect(() => {
    setLoading(true);

    apiCall("get", "/api/v2/project-item?item_per_page=1000")
      .then((response) => {
        setProjectItems(response?.data?.data?.items || []);
      })
      .catch((error) => {
        window.App.toastShow("error", t("error"), error.toString(), "", error.response);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const ActionCell = ({ row }) => {
    const roles = localStorage.getItem("roles") || "";

    return (
      <>
        {roles.toString().includes("role_") && (
          <Button
            icon="pi pi-eye"
            tooltip={t('view')}
            tooltipOptions={{ position: "left" }}
            onClick={() => loadProjectItem(row?.id)}
          />
        )}
      </>
    );
  };

  const kanbanColumns = useMemo(() => {
    const grouped = {};

    projectItems.forEach((item) => {
      const statusId = getStatusId(item) || "UNKNOWN";

      if (!grouped[statusId]) {
        grouped[statusId] = [];
      }

      grouped[statusId].push(item);
    });

    const orderedColumns = projectItemStatuses.map((status) => ({
      key: status.id,
      label: status.name,
      items: grouped[status.id] || []
    }));

    const unknownColumns = Object.keys(grouped)
      .filter((key) => {
        if (key === "UNKNOWN") return true;

        return !projectItemStatuses.some((status) => Number(status.id) === Number(key));
      })
      .map((key) => ({
        key,
        label: key === "UNKNOWN" ? "Nincs státusz" : getStatusLabelById(key),
        items: grouped[key]
      }));

    return [
      ...orderedColumns,
      ...unknownColumns
    ];
  }, [projectItems, projectItemStatuses, ganttHorizonDays, ganttScaleMode]);

  const { tasks, links, scales, columns, ganttStart, ganttEnd, lengthUnit, cellWidth } = useMemo(() => {
    const transformedTasks = projectItems
      .map((item) => {
        const start = getItemStartDate(item);
        const end = getItemEndDate(item);

        if (!start || !end) {
          return null;
        }

        const statusName = getStatusName(item);

        return {
          id: item.id,
          text: `${getProjectItemTitle(item)} — ${item?.project?.name || "N/A"}`,
          start,
          end,
          type: "task",
          status: statusName,
          status_id: getStatusId(item),
          progress: statusName.toLowerCase().includes("kész") ? 100 : 0,
          css: "gantt-plan-task"
        };
      })
      .filter(Boolean);

    const columns = [
      { id: "text", header: "Tétel", width: 320 },
      {
        id: "status",
        header: "Státusz",
        width: 150,
        cell: ({ row }) => renderProjectItemStatusBadge(row?.status)
      },
      { id: "start", header: "Kezdés", width: 140 },
      { id: "duration", header: "Időtartam", width: 100, align: "center" },
      { id: "actions", header: "", width: 90, cell: ActionCell }
    ];

    const taskDates = transformedTasks
      .map((task) => task.start)
      .filter(Boolean)
      .map((date) => date.getTime());

    const baseStart = taskDates.length > 0
      ? new Date(Math.min(...taskDates))
      : new Date();

    baseStart.setHours(0, 0, 0, 0);

    const baseEnd = new Date(baseStart);
    baseEnd.setDate(baseEnd.getDate() + ganttHorizonDays - 1);
    baseEnd.setHours(23, 59, 59, 999);

    const scales = ganttScaleMode === "day"
      ? [
          { unit: "month", step: 1, format: "%Y. %M" },
          { unit: "day", step: 1, format: "%j." }
        ]
      : [
          { unit: "day", step: 1, format: "%Y. %M %j." },
          { unit: "hour", step: 1, format: "%H:%i" }
        ];

    const lengthUnit = ganttScaleMode === "day" ? "day" : "hour";

    const cellWidth = ganttScaleMode === "day"
      ? (ganttHorizonDays > 30 ? 32 : 55)
      : 60;

    return {
      tasks: transformedTasks,
      links: [],
      scales,
      columns,
      ganttStart: baseStart,
      ganttEnd: baseEnd,
      lengthUnit,
      cellWidth
    };
  }, [projectItems, projectItemStatuses, ganttHorizonDays, ganttScaleMode]);

  const Theme = dark ? WillowDark : Willow;

  return (
    <div className="ProductionItemsComponent widget">
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{
            height: "5px",
            position: "absolute",
            top: 0,
            left: 0,
            borderRadius: 0,
            zIndex: 10
          }}
        />
      )}

      <div
        className="p-d-flex p-ai-center p-jc-between p-mb-3 p-p-2"
        style={{
          gap: 12,
          background: "var(--surface-b)",
          borderRadius: "6px"
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
          <i className="pi pi-chart-bar p-mr-2"></i>
          {t("productionItem")}
        </div>

        <div className="p-d-flex p-ai-center" style={{ gap: 10, flexWrap: "wrap" }}>
          <SelectButton
            value={viewType}
            options={viewTypeOptions}
            onChange={(e) => handleViewTypeChange(e.value)}
            disabled={loading}
          />

          {/* <Button
            icon={dark ? "pi pi-sun" : "pi pi-moon"}
            className="p-button-rounded p-button-text p-button-secondary"
            onClick={() => setDark(!dark)}
            tooltip={dark ? t('lightMode') : t('darkMode')}
          /> */}
          {viewType === "gantt" && (
            <>
              <Dropdown
                value={ganttScaleMode}
                options={ganttScaleOptions}
                onChange={(e) => handleGanttScaleModeChange(e.value)}
                disabled={loading}
                placeholder={t('resolutionPlaceholder')}style={{ minWidth: 120 }}
              />

              <Dropdown
                value={ganttHorizonDays}
                options={ganttHorizonOptions}
                onChange={(e) => handleGanttHorizonChange(e.value)}
                disabled={loading}
                placeholder={t('timeHorizonPlaceholder')}style={{ minWidth: 130 }}
              />
            </>
          )}
        </div>
      </div>

      {viewType === "gantt" && (
        <div
          style={{
            height: "calc(100vh - 250px)",
            minHeight: 600,
            background: dark ? "#333" : "#fff",
            borderRadius: 6,
            overflow: "auto",
            border: "1px solid var(--surface-d)"
          }}
        >
          {tasks.length > 0 ? (
            <Theme>
              <Gantt
                tasks={tasks}
                links={links}
                scales={scales}
                columns={columns}
                start={ganttStart}
                end={ganttEnd}
                projectStart={ganttStart}
                projectEnd={ganttEnd}
                lengthUnit={lengthUnit}
                cellWidth={cellWidth}
              />
            </Theme>
          ) : (
            <div
              className="p-d-flex p-ai-center p-jc-center p-flex-column"
              style={{ height: "100%", opacity: 0.5 }}
            >
              <i className="pi pi-filter-slash p-mb-2" style={{ fontSize: "2rem" }}></i>
              <span>
                {loading ? t('loadingData') : t('noDisplayableProductionItem')}
              </span>
            </div>
          )}
        </div>
      )}

      {viewType === "kanban" && (
        <div className="production-kanban-wrapper">
          {kanbanColumns.length > 0 ? (
            <div className="production-kanban-board">
              {kanbanColumns.map((column) => (
                <div className="production-kanban-column" key={column.key}>
                  <div className="production-kanban-column-header">
                    <span className="production-kanban-column-title">
                      {column.label}
                    </span>

                    <span className="production-kanban-count">
                      {column.items.length}
                    </span>
                  </div>

                  <div
                    className="production-kanban-items"
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();

                      if (column.key !== "UNKNOWN") {
                        updateProjectItemStatus(draggedItemId, column.key);
                      } else {
                        setDraggedItemId(null);
                      }
                    }}
                  >
                    {column.items.map((item) => (
                      <div
                        className="production-kanban-card"
                        key={item.id}
                        draggable={!statusUpdating}
                        onDragStart={() => {
                          setDraggedItemId(item.id);
                        }}
                        onDragEnd={() => {
                          setDraggedItemId(null);
                        }}
                        style={{
                          opacity: draggedItemId === item.id ? 0.5 : 1,
                          cursor: statusUpdating ? "not-allowed" : "move"
                        }}
                      >
                        <div className="production-kanban-card-header">
                          <div className="production-kanban-card-title">
                            {getProjectItemTitle(item)}
                          </div>

                          <Button
                            icon="pi pi-eye"
                            tooltip={t('view')}
                            tooltipOptions={{ position: "left" }}
                            onClick={() => loadProjectItem(item?.id)}
                          />
                        </div>

                        <div className="production-kanban-card-subtitle">
                          {getProjectItemSubtitle(item)}
                        </div>

                        <div className="production-kanban-card-meta">
                          <div>
                            <i className="pi pi-calendar p-mr-1"></i>
                            {t('columnCreatedAt')}:
                            {" "}
                            {getDateValue(item?.created_at)}
                          </div>

                          <div>
                            <i className="pi pi-refresh p-mr-1"></i>
                            {t('columnUpdatedAt')}:
                            {" "}
                            {getDateValue(item?.updated_at)}
                          </div>
                        </div>

                        <div className="production-kanban-card-footer">
                          <span>
                            {item?.quantity || 0} {item?.unit?.name || ""}
                          </span>

                          {item?.production_items?.length > 0 && (
                            <span>
                              {item.production_items.length} {t('task')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="p-d-flex p-ai-center p-jc-center p-flex-column"
              style={{ height: "100%", opacity: 0.5 }}
            >
              <i className="pi pi-filter-slash p-mb-2" style={{ fontSize: "2rem" }}></i>
              <span>
                {loading ? t('loadingData') : t('noDisplayableProductionItem')}
              </span>
            </div>
          )}
        </div>
      )}

      <Dialog
        header={t('productionItemDetails')}
        visible={dialogVisible}
        style={{ width: "50vw" }}
        onHide={() => {
          setDialogVisible(false);
          setSelectedItem(null);
        }}
      >
        {dialogLoading && (
          <ProgressBar mode="indeterminate" style={{ height: "5px" }} />
        )}

        {!dialogLoading && selectedItem && (
          <>
            <div className="p-grid">
              <div className="p-col-12">
                <div
                  className="p-p-3"
                  style={{
                    border: "1px solid var(--surface-d)",
                    borderRadius: 6
                  }}
                >
                  <h4 className="p-mt-0 p-mb-3 text-primary">
                    {t("projectItem")}
                  </h4>

                  <p>
                    <strong>{t('name')}:</strong>{" "}
                    {selectedItem?.project_item?.name || "-"}
                  </p>

                  <p>
                    <strong>{t('status')}:</strong>{" "}
                    {selectedItem ? renderProjectItemStatusBadge(selectedItem) : "-"}
                  </p>

                  <p>
                    <strong>{t('quantity')}:</strong>{" "}
                    {selectedItem?.project_item?.quantity || 0} {selectedItem?.project_item?.unit?.name || ""}
                  </p>

                  <p>
                    <strong>{t('columnNetAmount')}:</strong>{" "}
                    {selectedItem?.project_item?.net_amount ?? "-"}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
{/* 
        <p>
          <strong>{t("externalReference")}:</strong>{" "}
          {selectedItem?.external_reference || "-"}
        </p> */}

        {/* <p>
          <strong>Kapcsolódó gyártási feladatok:</strong>{" "}
          {selectedItem?.production_items?.length || 0}
        </p> */}

        <ProductionMetricsMeasuresSummary
          projectItemId={getSelectedProjectItemId()}
          refreshKey={metricsRefreshKey}
          t={t}
        />

        <ProductionWorkflowMetricsForm
          projectItemId={getSelectedProjectItemId()}
          productionItemId={selectedItem?.production_items?.[0]?.id}
          t={t}
          onSaved={() => setMetricsRefreshKey((prev) => prev + 1)}
        />

        <Button icon="pi pi-download" className="p-button-secondary p-mr-2"
                tooltip="Letöltés" tooltipOptions={{position: 'left'}}
                onClick={() => {
                  if (!selectedItem) return;
                  apiCall('get', `/api/v2/project-item/${selectedItem.id}/download/pdf`) 
                    .then((response) => {
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `project_item_${selectedItem.id}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                    })
                    .catch((error) => {
                      window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                    });
                }}
        ></Button>
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(ProductionItemsComponent);
